import {t, getLanguage} from "../i18n/index.js";

// Main-thread Web Worker lifecycle for "Areal og volumen". One job at a
// time by design (starting a new one, or an explicit cancel, always
// terminates whatever was running) - a hard `worker.terminate()` is used
// for cancellation rather than a cooperative "please stop" flag threaded
// through the (protected, untouched) measurement core: the whole
// computation completes in low single-digit seconds even at 1M raw points
// (Fase 5.5's own benchmark), so there is no long-running inner loop that
// needs graceful interruption - terminate() is simple, always effective,
// and can never leave a zombie worker or a stale result behind (every
// message carries the job's own id; a message from an already-cancelled
// job's worker cannot arrive after terminate()).
//
// ── Fase 7: guaranteed terminal state (task section 13) ────────────────────
// A real-world test previously left a job in a state where none of
// onResult/onError ever fired - main thread just sat on a stale progress
// message forever. `worker.onmessageerror` (a structured-clone failure
// receiving a message - previously unhandled here) is now wired up
// alongside onmessage/onerror, so EVERY way a worker can stop communicating
// resolves to exactly one terminal state: SUCCESS, ERROR, or CANCELLED -
// never silently vanished. Each terminal transition is logged under the
// shared `[Volume job N]` tag for diagnosis.
//
// ── Watchdog support (task section 12) ─────────────────────────────────────
// `getActiveJobProgress()` exposes the active job's last-seen progress
// stage and when it was last seen, so the UI (volumeTool.js) can show
// "taking longer than expected" without this module needing to own any
// UI/timer itself.

let activeJob = null; // {jobId, worker, lastStage, lastProgressAt}
let jobCounter = 0;

function logTerminal(jobId, state, detail){
	console.log(`[Volume job ${jobId}] terminal state: ${state}${detail ? " (" + detail + ")" : ""}`);
}

// Audit finding: this used to terminate the worker and null out `activeJob`
// WITHOUT ever calling the job's own onError/onResult - so a caller `await`ing
// startVolumeJob()'s Promise (see volumeTool.js's `runVolumeCapture`) hung
// forever on an ESC-cancel mid-computation: the watchdog interval was never
// cleared, the capture visual was never removed from the scene, and the
// tool never returned to an idle state. `onCancelled` (stored on `activeJob`
// by startVolumeJob(), below) closes that gap by settling the SAME promise
// the caller is already waiting on - volumeTool.js already special-cases its
// own `cancelled` flag and ignores whatever this resolves with, so which
// callback fires or what message it carries doesn't matter, only that one
// of them fires at all.
export function cancelActiveVolumeJob(){
	if(!activeJob) return;
	const {jobId, worker, onCancelled} = activeJob;
	worker.terminate();
	activeJob = null;
	logTerminal(jobId, "CANCELLED");
	if(onCancelled) onCancelled();
}

export function hasActiveVolumeJob(){
	return activeJob !== null;
}

// Returns {jobId, lastStage, lastProgressAt} for the currently active job,
// or null if none is running - used by the watchdog UI to detect a job
// that has gone quiet for an unreasonably long time.
export function getActiveJobProgress(){
	if(!activeJob) return null;
	return {jobId: activeJob.jobId, lastStage: activeJob.lastStage, lastProgressAt: activeJob.lastProgressAt};
}

// positions/polygon: Float64Array (xyz interleaved, world coords) -
// `positions` is transferred (its buffer becomes unusable in the caller
// after this call). callbacks: {onProgress(stage), onResult(msg),
// onError(message, technical)} - exactly one of onResult/onError fires,
// exactly once, for a given job (never for a job that was since
// cancelled/superseded). `jobId`: optional externally-supplied shared job
// label (see volumeJobId.js) so ROI-load and Worker-compute logging share
// one number; falls back to an internal counter if omitted (existing
// tests/callers that don't care about the shared label keep working
// unchanged).
export function startVolumeJob(positions, polygon, {onProgress, onResult, onError, jobId: externalJobId}){
	cancelActiveVolumeJob();

	const jobId = externalJobId !== undefined ? externalJobId : ++jobCounter;
	const workerPath = Potree.scriptPath + "/workers/volumeWorker.js";
	// {type:"module"} is required here (unlike the other, plain-classic-script
	// decoder workers in this codebase): volumeWorker.js deliberately keeps a
	// top-level `export {compute}` in its bundled output so compute() stays
	// directly unit-testable (see volumeWorker.test.js) - a top-level `export`
	// is a SyntaxError in a classic worker script, so it must be loaded as a
	// module worker (found by inspecting the actual rollup build output, not
	// assumed).
	const worker = new Worker(workerPath, {type: "module"});
	activeJob = {
		jobId, worker, lastStage: "starting", lastProgressAt: Date.now(),
		// see cancelActiveVolumeJob()'s own comment - guarantees this job's
		// promise settles even when it's cancelled/superseded, not just on a
		// real worker message
		onCancelled: () => onError(t("Annulleret."), null),
	};

	const finish = () => {
		if(activeJob && activeJob.jobId === jobId){
			activeJob.worker.terminate();
			activeJob = null;
		}
	};

	worker.onmessage = (e) => {
		const msg = e.data;
		if(!activeJob || activeJob.jobId !== jobId || msg.jobId !== jobId) return; // stale - ignore, never commit
		if(msg.type === "progress"){
			activeJob.lastStage = msg.stage;
			activeJob.lastProgressAt = Date.now();
			onProgress(msg.stage);
			return;
		}
		if(msg.type === "result"){
			finish();
			logTerminal(jobId, "SUCCESS");
			onResult(msg);
			return;
		}
		if(msg.type === "error"){
			finish();
			logTerminal(jobId, "ERROR", msg.message);
			onError(msg.message, msg.technical);
			return;
		}
	};
	worker.onerror = (e) => {
		if(!activeJob || activeJob.jobId !== jobId) return;
		finish();
		const message = (e && e.message) || t("Uventet Worker-fejl.");
		logTerminal(jobId, "ERROR", message);
		onError(message, null);
	};
	// A structured-clone failure on a RECEIVED message fires this instead of
	// onmessage - without a handler, the job would just go quiet forever
	// (exactly the unexplained "vanished job" symptom found during real-world
	// testing). Guaranteeing a terminal state here closes that gap.
	worker.onmessageerror = (e) => {
		if(!activeJob || activeJob.jobId !== jobId) return;
		finish();
		const message = t("Worker-svaret kunne ikke læses (messageerror).");
		logTerminal(jobId, "ERROR", message);
		onError(message, null);
	};

	worker.postMessage({type: "compute", jobId, positions, polygon, language: getLanguage()}, [positions.buffer]);
	return jobId;
}
